import { useEffect, useRef, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Clock3,
  Globe2,
  MapPin,
  Pause,
  Play,
  Phone,
} from "lucide-react";
import type { MenuCategory, MenuDraft, MenuLanguage, MenuSliderEntry } from "@qassist/schemas";

export interface MenuRendererProps {
  menu: MenuDraft;
  language: MenuLanguage;
  assetUrls: Record<string, string>;
  route: { categoryId?: string; itemId?: string };
  interactive?: boolean;
  onLanguageChange?(language: MenuLanguage): void;
  onHome?(): void;
  onCategory?(categoryId: string): void;
  onItem?(categoryId: string, itemId: string): void;
}

function MenuSlider({
  entries,
  language,
  assetUrls,
}: {
  entries: MenuSliderEntry[];
  language: MenuLanguage;
  assetUrls: Record<string, string>;
}) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [pageVisible, setPageVisible] = useState(
    typeof document === "undefined" || document.visibilityState === "visible",
  );
  const startX = useRef<number | null>(null);
  const reducedMotion =
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  useEffect(() => {
    if (entries.length < 2 || paused || focused || hovered || reducedMotion || !pageVisible) return;
    const timer = window.setInterval(
      () => setIndex((value) => (value + 1) % entries.length),
      5_000,
    );
    return () => window.clearInterval(timer);
  }, [entries.length, focused, hovered, pageVisible, paused, reducedMotion]);
  useEffect(() => {
    const updateVisibility = () => setPageVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", updateVisibility);
    return () => document.removeEventListener("visibilitychange", updateVisibility);
  }, []);
  useEffect(
    () => setIndex((value) => Math.min(value, Math.max(0, entries.length - 1))),
    [entries.length],
  );
  if (!entries.length) return null;
  const entry = entries[index]!;
  const move = (offset: number) =>
    setIndex((value) => (value + offset + entries.length) % entries.length);
  return (
    <section
      className="restaurant-menu__slider"
      aria-roledescription="carousel"
      aria-label={language === "ar" ? "معرض الصور" : "Image gallery"}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
      onPointerDown={(event) => {
        startX.current = event.clientX;
      }}
      onPointerUp={(event) => {
        if (startX.current === null) return;
        const delta = event.clientX - startX.current;
        if (Math.abs(delta) > 45) move(delta > 0 ? -1 : 1);
        startX.current = null;
      }}
    >
      <figure>
        <img src={assetUrls[entry.assetId]} alt={entry.alt[language]} />
        {entry.caption[language] ? <figcaption>{entry.caption[language]}</figcaption> : null}
      </figure>
      {entries.length > 1 ? (
        <div className="restaurant-menu__slider-controls">
          <button
            type="button"
            onClick={() => move(-1)}
            aria-label={language === "ar" ? "الصورة السابقة" : "Previous image"}
          >
            <ChevronLeft />
          </button>
          <span aria-live="polite">
            {index + 1} / {entries.length}
          </span>
          <span
            className="restaurant-menu__slider-dots"
            aria-label={language === "ar" ? "اختيار صورة" : "Choose image"}
          >
            {entries.map((slide, slideIndex) => (
              <button
                type="button"
                key={slide.id}
                className={slideIndex === index ? "is-active" : ""}
                onClick={() => setIndex(slideIndex)}
                aria-label={`${language === "ar" ? "الصورة" : "Image"} ${slideIndex + 1}`}
                aria-current={slideIndex === index ? "true" : undefined}
              />
            ))}
          </span>
          <button
            type="button"
            onClick={() => move(1)}
            aria-label={language === "ar" ? "الصورة التالية" : "Next image"}
          >
            <ChevronRight />
          </button>
          <button
            type="button"
            onClick={() => setPaused((value) => !value)}
            aria-pressed={paused}
            aria-label={
              paused
                ? language === "ar"
                  ? "تشغيل العرض"
                  : "Play slideshow"
                : language === "ar"
                  ? "إيقاف العرض"
                  : "Pause slideshow"
            }
          >
            {paused ? <Play /> : <Pause />}
          </button>
        </div>
      ) : null}
    </section>
  );
}

const dayLabels = {
  sunday: { ar: "الأحد", en: "Sunday" },
  monday: { ar: "الاثنين", en: "Monday" },
  tuesday: { ar: "الثلاثاء", en: "Tuesday" },
  wednesday: { ar: "الأربعاء", en: "Wednesday" },
  thursday: { ar: "الخميس", en: "Thursday" },
  friday: { ar: "الجمعة", en: "Friday" },
  saturday: { ar: "السبت", en: "Saturday" },
} as const;

export function MenuRenderer({
  menu,
  language,
  assetUrls,
  route,
  interactive = true,
  onLanguageChange,
  onHome,
  onCategory,
  onItem,
}: MenuRendererProps) {
  const profile = menu.profile;
  const category = route.categoryId
    ? menu.categories.find((entry) => entry.id === route.categoryId)
    : undefined;
  const item =
    category && route.itemId
      ? category.items.find((entry) => entry.id === route.itemId)
      : undefined;
  const formatPrice = (price: string) =>
    new Intl.NumberFormat(language === "ar" ? "ar-QA" : "en-QA", {
      style: "currency",
      currency: profile.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(price));
  const direction = language === "ar" ? "rtl" : "ltr";
  const text = (value: { ar: string; en: string }) => value[language];
  const showCategory = (next: MenuCategory) => interactive && onCategory?.(next.id);
  return (
    <div className="restaurant-menu" data-theme={profile.themeKey} dir={direction} lang={language}>
      <header className="restaurant-menu__header">
        <button
          type="button"
          className="restaurant-menu__brand"
          onClick={onHome}
          disabled={!interactive}
          aria-label={`${language === "ar" ? "الرئيسية" : "Home"} — ${
            text(profile.name) || (language === "ar" ? "قائمة المطعم" : "Restaurant menu")
          }`}
        >
          {profile.logoAssetId ? (
            <img src={assetUrls[profile.logoAssetId]} alt="" />
          ) : (
            <span className="restaurant-menu__monogram" aria-hidden="true">
              {text(profile.name).slice(0, 2)}
            </span>
          )}
          <span>
            {text(profile.name) || (language === "ar" ? "قائمة المطعم" : "Restaurant menu")}
          </span>
        </button>
        <button
          type="button"
          className="restaurant-menu__language"
          onClick={() => onLanguageChange?.(language === "ar" ? "en" : "ar")}
          disabled={!interactive}
        >
          <Globe2 /> {language === "ar" ? "English" : "العربية"}
        </button>
      </header>

      {item && category ? (
        <main className="restaurant-menu__main restaurant-menu__detail">
          <button
            type="button"
            className="restaurant-menu__back"
            onClick={() => onCategory?.(category.id)}
            disabled={!interactive}
          >
            <ChevronLeft />
            {language === "ar" ? "العودة إلى" : "Back to"} {text(category.name)}
          </button>
          {item.imageAssetId ? (
            <img
              className="restaurant-menu__detail-image"
              src={assetUrls[item.imageAssetId]}
              alt=""
            />
          ) : null}
          <span className="restaurant-menu__eyebrow">{text(category.name)}</span>
          <h1>{text(item.name)}</h1>
          <strong className="restaurant-menu__price">{formatPrice(item.price)}</strong>
          {text(item.description) ? <p>{text(item.description)}</p> : null}
          {item.variants.length ? (
            <section className="restaurant-menu__variants" aria-labelledby="variant-heading">
              <h2 id="variant-heading">
                {language === "ar" ? "الأحجام والخيارات" : "Sizes and variants"}
              </h2>
              {item.variants.map((variant) => (
                <div key={variant.id}>
                  <span>{text(variant.name)}</span>
                  <strong>{formatPrice(variant.price)}</strong>
                </div>
              ))}
            </section>
          ) : null}
        </main>
      ) : category ? (
        <main className="restaurant-menu__main">
          <button
            type="button"
            className="restaurant-menu__back"
            onClick={onHome}
            disabled={!interactive}
          >
            <ChevronLeft />
            {language === "ar" ? "كل الفئات" : "All categories"}
          </button>
          <div className="restaurant-menu__title">
            <span className="restaurant-menu__eyebrow">
              {language === "ar" ? "الفئة" : "Category"}
            </span>
            <h1>{text(category.name)}</h1>
            {text(category.description) ? <p>{text(category.description)}</p> : null}
          </div>
          <MenuSlider entries={category.slides} language={language} assetUrls={assetUrls} />
          <section className="restaurant-menu__items" aria-label={text(category.name)}>
            {category.items.map((entry) => (
              <button
                type="button"
                className="restaurant-menu__item"
                key={entry.id}
                onClick={() => onItem?.(category.id, entry.id)}
                disabled={!interactive}
              >
                {entry.imageAssetId ? (
                  <img src={assetUrls[entry.imageAssetId]} alt="" loading="lazy" />
                ) : (
                  <span className="restaurant-menu__image-fallback" aria-hidden="true" />
                )}
                <span className="restaurant-menu__item-copy">
                  <strong>{text(entry.name)}</strong>
                  {text(entry.description) ? <small>{text(entry.description)}</small> : null}
                </span>
                <span className="restaurant-menu__price">{formatPrice(entry.price)}</span>
                <ChevronRight aria-hidden="true" />
              </button>
            ))}
          </section>
        </main>
      ) : (
        <main className="restaurant-menu__main">
          <MenuSlider entries={menu.homeSlides} language={language} assetUrls={assetUrls} />
          <section className="restaurant-menu__intro">
            {profile.coverAssetId ? <img src={assetUrls[profile.coverAssetId]} alt="" /> : null}
            <div>
              <span className="restaurant-menu__eyebrow">
                {language === "ar" ? "أهلاً وسهلاً" : "Welcome"}
              </span>
              <h1>
                {text(profile.name) || (language === "ar" ? "قائمة المطعم" : "Restaurant menu")}
              </h1>
              {text(profile.description) ? <p>{text(profile.description)}</p> : null}
            </div>
          </section>
          <section className="restaurant-menu__categories" aria-labelledby="categories-heading">
            <div className="restaurant-menu__section-heading">
              <span>01</span>
              <h2 id="categories-heading">{language === "ar" ? "الفئات" : "Categories"}</h2>
            </div>
            <div className="restaurant-menu__category-grid">
              {menu.categories.map((entry, index) => (
                <button
                  type="button"
                  key={entry.id}
                  onClick={() => showCategory(entry)}
                  disabled={!interactive}
                >
                  {entry.coverAssetId ? (
                    <img src={assetUrls[entry.coverAssetId]} alt="" loading="lazy" />
                  ) : (
                    <span className="restaurant-menu__image-fallback" />
                  )}
                  <span>
                    <small>{String(index + 1).padStart(2, "0")}</small>
                    <strong>{text(entry.name)}</strong>
                    {text(entry.description) ? <em>{text(entry.description)}</em> : null}
                  </span>
                  <ChevronRight />
                </button>
              ))}
            </div>
          </section>
          <section className="restaurant-menu__information" aria-labelledby="information-heading">
            <div className="restaurant-menu__section-heading">
              <span>02</span>
              <h2 id="information-heading">
                {language === "ar" ? "معلومات المطعم" : "Restaurant information"}
              </h2>
            </div>
            <div className="restaurant-menu__info-grid">
              {text(profile.address) ? (
                <a
                  href={profile.mapUrl || undefined}
                  target={profile.mapUrl ? "_blank" : undefined}
                  rel="noreferrer"
                >
                  <MapPin />
                  <span>{text(profile.address)}</span>
                </a>
              ) : null}
              {profile.phone ? (
                <a href={`tel:${profile.phone}`}>
                  <Phone />
                  <span>{profile.phone}</span>
                </a>
              ) : null}
              <div className="restaurant-menu__hours">
                <Clock3 />
                <div>
                  {profile.openingHours.map((entry) => (
                    <p key={entry.day}>
                      <strong>{dayLabels[entry.day][language]}</strong>
                      <span>
                        {entry.closed
                          ? language === "ar"
                            ? "مغلق"
                            : "Closed"
                          : `${entry.opensAt} – ${entry.closesAt}`}
                      </span>
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </main>
      )}
      <footer className="restaurant-menu__footer">
        <span>{text(profile.name)}</span>
        <nav aria-label={language === "ar" ? "روابط المطعم" : "Restaurant links"}>
          {profile.websiteUrl ? (
            <a href={profile.websiteUrl} target="_blank" rel="noreferrer">
              {language === "ar" ? "الموقع" : "Website"}
            </a>
          ) : null}
          {profile.whatsapp ? (
            <a
              href={
                /^https:\/\/(?:wa\.me|api\.whatsapp\.com)\//i.test(profile.whatsapp)
                  ? profile.whatsapp
                  : `https://wa.me/${profile.whatsapp.replace(/\D/g, "")}`
              }
              target="_blank"
              rel="noreferrer"
            >
              WhatsApp
            </a>
          ) : null}
          {profile.socialLinks.map((link) =>
            link.url ? (
              <a key={link.id} href={link.url} target="_blank" rel="noreferrer">
                {link.network}
              </a>
            ) : null,
          )}
        </nav>
      </footer>
    </div>
  );
}
